import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api/axios';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { CampaignNodeData } from '@repo/shared-types';

function useConfigurations() {
  return useQuery({
    queryKey: ['configurations'],
    queryFn: async () => {
      const res = await apiClient.get<{ id: string; name: string }[]>('/configurations');
      return res.data;
    },
  });
}

function useWhatsAppNumbers() {
  return useQuery({
    queryKey: ['whatsapp-numbers'],
    queryFn: async () => {
      const res = await apiClient.get<
        {
          id: string;
          phone_number: string;
          friendly_name?: string | null;
          inbound_configuration_name?: string | null;
        }[]
      >('/whatsapp/numbers');
      return res.data;
    },
  });
}

interface CampaignConfigProps {
  data: CampaignNodeData;
  onChange: (data: CampaignNodeData) => void;
}

export const CampaignConfig = ({ data, onChange }: CampaignConfigProps) => {
  const { data: configurations } = useConfigurations();
  const { data: whatsappNumbers } = useWhatsAppNumbers();

  const update = (fields: Partial<CampaignNodeData>) => {
    onChange({ ...data, ...fields });
  };

  const bodyFields = data.waTemplateBodyFields ?? [];

  const updateBodyField = (index: number, value: string) => {
    update({ waTemplateBodyFields: bodyFields.map((f, i) => (i === index ? value : f)) });
  };

  const addBodyField = () => {
    update({ waTemplateBodyFields: [...bodyFields, ''] });
  };

  const removeBodyField = (index: number) => {
    update({ waTemplateBodyFields: bodyFields.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Label</label>
        <Input
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Create Campaign"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Items Key</label>
        <Input
          value={data.itemsKey || ''}
          onChange={(e) => update({ itemsKey: e.target.value })}
          placeholder="collected"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Context path holding the array of items (e.g. a Collect node&apos;s output key).
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Configuration</label>
        <select
          value={data.configurationId || ''}
          onChange={(e) => update({ configurationId: e.target.value })}
          className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
        >
          <option value="">Select configuration...</option>
          {configurations?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[10px] text-gray-500">
          The configuration the campaign dials/sends with.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Campaign Name</label>
        <VariableAutocomplete
          value={data.name || ''}
          onChange={(v) => update({ name: v })}
          placeholder="e.g. Daily follow-ups {{date_offset:0}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Include a date token to keep daily names unique — a name conflict resolves to
          already_exists.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Runs At (optional)</label>
        <VariableAutocomplete
          value={data.runsAt || ''}
          onChange={(v) => update({ runsAt: v })}
          placeholder="YYYY-MM-DD (defaults to today)"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Channel</label>
        <Select
          value={data.channel || 'PSTN'}
          onValueChange={(v) => update({ channel: v as 'PSTN' | 'WHATSAPP' })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PSTN">PSTN (voice)</SelectItem>
            <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {data.channel === 'WHATSAPP' && (
        <>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              Send From (WhatsApp number)
            </label>
            <select
              value={data.waFromNumberId || ''}
              onChange={(e) => update({ waFromNumberId: e.target.value || undefined })}
              className="border-input bg-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm shadow-sm focus-visible:ring-1 focus-visible:outline-none"
            >
              <option value="">Use the configuration&apos;s own number</option>
              {whatsappNumbers?.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.friendly_name ? `${n.friendly_name} — ${n.phone_number}` : n.phone_number}
                  {n.inbound_configuration_name
                    ? ` (inbound: ${n.inbound_configuration_name})`
                    : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[10px] text-gray-500">
              Required when the target configuration does not own a WhatsApp number — a number can
              be the inbound number of only one configuration.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">WA Template Name</label>
            <Input
              value={data.waTemplateName || ''}
              onChange={(e) => update({ waTemplateName: e.target.value })}
              placeholder="Template name"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-400">
              WA Template Language
            </label>
            <Input
              value={data.waTemplateLanguage || ''}
              onChange={(e) => update({ waTemplateLanguage: e.target.value })}
              placeholder="e.g. en"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-gray-400">WA Template Body Fields</label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={addBodyField}
              >
                + Add
              </Button>
            </div>
            <div className="space-y-2">
              {bodyFields.map((field, index) => (
                <div key={index} className="flex gap-1">
                  <Input
                    value={field}
                    onChange={(e) => updateBodyField(index, e.target.value)}
                    placeholder="Dotted key on contact data"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs text-red-500 hover:text-red-600"
                    onClick={() => removeBodyField(index)}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-gray-500">
              Ordered body variables, as dotted keys on the contact&apos;s data.
            </p>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={data.waCallOnReply ?? false}
              onChange={(e) => update({ waCallOnReply: e.target.checked })}
            />
            Call the contact when they reply
          </label>

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={data.waCallOnUndelivered ?? false}
              onChange={(e) => update({ waCallOnUndelivered: e.target.checked })}
            />
            Call the contact if the message never reaches them
          </label>
        </>
      )}

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Contact Name</label>
        <VariableAutocomplete
          value={data.contactName || ''}
          onChange={(v) => update({ contactName: v })}
          placeholder="{{item.name}}"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Contact Phone</label>
        <VariableAutocomplete
          value={data.contactPhone || ''}
          onChange={(v) => update({ contactPhone: v })}
          placeholder="{{item.phone}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Per-contact fields are templates resolved per row, with the row exposed as {'{{item...}}'}
          .
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">
          Contact Data Key (optional)
        </label>
        <Input
          value={data.contactDataKey || ''}
          onChange={(e) => update({ contactDataKey: e.target.value })}
          placeholder='e.g. "data"'
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Dotted path inside the item whose object becomes the contact&apos;s data verbatim.
        </p>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={data.activate ?? true}
          onChange={(e) => update({ activate: e.target.checked })}
        />
        Activate the campaign after creating it
      </label>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Output Key</label>
        <Input
          value={data.outputKey || 'campaign'}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="campaign"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          The created campaign (id, name, status) is available under this key downstream.
        </p>
      </div>
    </div>
  );
};
